import { useMemo } from 'react';
import { useAppSelector } from '../../../app/hooks';
import { CubeDimensionEnum } from '../../../services/data/impl/cube/cubeSlice';
import { convertColumnUnits } from '../../commonUtils';
import useCurrServicesAndMeasures from '../../customerSummary/hooks/useCurrServicesAndMeasures';
import {
  selectColumnsExistInDbData,
  selectScanDataTableExists,
} from '../../duckDB/duckDBSlice';
import useDuckQueryScanData from '../../duckDB/hooks/useDuckQueryScanData';
import { useUnits } from '../../settings/hooks/useUnits';
import { ScanDataEnum } from '../scanDataDefinitions';
import { filterScanDataColumns } from './useColumnsExistInDB';

export enum OperatorEnum {
  eq = '=',
  neq = '<>',
  gt = '>',
  lt = '<',
  gte = '>=',
  lte = '<=',
  like = 'like',
  nlike = 'not like',
  between = 'between',
  in = 'in',
}

export type Order = [ScanDataEnum | string, 'asc' | 'desc'][];

export const operatorToExpressionMap: {
  [key: string]: (
    col: string,
    value: string | number | string[] | number[] | null
  ) => string;
} = {
  [OperatorEnum.eq]: (col, value) => {
    if (value === null) return `${col} IS NULL`;
    if (typeof value === 'string') {
      return `${col} ${OperatorEnum.eq} '${value}'`;
    }
    if (Array.isArray(value)) {
      return `${col} in (${value.join(', ')})`;
    }

    return `${col} ${OperatorEnum.eq} ${value}`;
  },
  [OperatorEnum.neq]: (col, value) => {
    if (value === null) return `${col} IS NOT NULL`;
    if (typeof value === 'string') {
      return `${col} ${OperatorEnum.neq} '${value}'`;
    }
    return `${col} ${OperatorEnum.neq} ${value}`;
  },
  [OperatorEnum.gt]: (col, value) => `${col} ${OperatorEnum.gt} ${value}`,
  [OperatorEnum.lt]: (col, value) => `${col} ${OperatorEnum.lt} ${value}`,
  [OperatorEnum.gte]: (col, value) => `${col} ${OperatorEnum.gte} ${value}`,
  [OperatorEnum.lte]: (col, value) => `${col} ${OperatorEnum.lte} ${value}`,
  [OperatorEnum.like]: (col, value) => `${col} ${OperatorEnum.like} '${value}'`,
  [OperatorEnum.nlike]: (col, value) =>
    `${col} ${OperatorEnum.nlike} '${value}'`,
  [OperatorEnum.between]: (col, value) => {
    if (Array.isArray(value)) {
      if (value.length !== 2) {
        throw new Error('Between operator requires exactly 2 values');
      }

      if (typeof value[0] === 'string' && typeof value[1] === 'string') {
        return `${col} ${OperatorEnum.between} '${value[0]}' AND '${value[1]}'`;
      }
      return `${col} ${OperatorEnum.between} ${value[0]} AND ${value[1]}`;
    }
    throw new Error('Between operator requires an array of 2 values');
  },
  [OperatorEnum.in]: (col, value) => {
    if (Array.isArray(value)) {
      return `${col} in (${value
        .map((el) => {
          if (typeof el === 'string') {
            return `'${el}'`;
          }
          return `${el}`;
        })
        .join(', ')})`;
    }
    throw new Error('In operator requires an array of values');
  },
};

export type ScanDataFilter = {
  col: string;
  op: OperatorEnum;
  value: string | string[] | number | number[] | null;
};

type Props = {
  from?: string;
  cols: string[];
  distinct?: boolean;
  distinctOn?: string[];
  filters?: ScanDataFilter[];
  filtersSQL?: string[];
  group?: ScanDataEnum[] | string[];
  having?: ScanDataFilter[];
  order?: Order;
  limit?: number;
  offset?: number;
  disableClassFilter?: boolean;
  disableUnitsTransform?: boolean;
  useUnitsTransformSubstringMatching?: boolean;
  skip?: boolean;
  cte?: string;
};

export const SCAN_DATA_TABLE_NAME = 'scanData';

export function useScanDataQuery<T>({
  from,
  cols,
  distinct,
  distinctOn,
  filters,
  filtersSQL,
  group,
  having,
  order,
  limit,
  offset,
  disableClassFilter,
  disableUnitsTransform,
  useUnitsTransformSubstringMatching,
  skip,
  cte = '',
}: Props) {
  const units = useUnits();
  const { servicesAndMeasuresWithFeatureFilter } = useCurrServicesAndMeasures();
  const scanDataTableExists = useAppSelector(selectScanDataTableExists);
  const columnsExistInDbData = useAppSelector(selectColumnsExistInDbData);

  // Create base query with table name provided in options
  const query = useMemo(() => {
    if (
      !columnsExistInDbData ||
      !columnsExistInDbData.data ||
      columnsExistInDbData.isLoading ||
      columnsExistInDbData.error
    )
      return null;

    // Transform columns to include unit conversions if needed
    const filteredAvailableCols = filterScanDataColumns(
      cols,
      columnsExistInDbData.data
    );
    const colsUnitsConverted = convertColumnUnits(
      filteredAvailableCols,
      units,
      disableUnitsTransform,
      useUnitsTransformSubstringMatching
    );

    // Before forming the query, check if colsUnitsConverted is empty
    if (colsUnitsConverted.length === 0) {
      // console.error('No columns specified for the SELECT statement.');
      return '';
    }
    const select = `${cte}SELECT
    ${distinct ? 'DISTINCT' : ''}
    ${distinctOn ? `ON (\n\t${distinctOn.join(',\n\t')}\n)` : ''} 
    ${colsUnitsConverted
      .map((columnConversion) => columnConversion.converted)
      .join(',\n\t')}`;

    const whereStatements = [];

    if (filters) {
      filters.forEach(({ col, value, op }) =>
        whereStatements.push(operatorToExpressionMap[op](col, value))
      );
    }

    if (
      !disableClassFilter &&
      servicesAndMeasuresWithFeatureFilter.currService
    ) {
      whereStatements.push(
        operatorToExpressionMap[OperatorEnum.eq](
          ScanDataEnum.class_name,
          servicesAndMeasuresWithFeatureFilter.currService[
            CubeDimensionEnum.className
          ]
        ),
        operatorToExpressionMap[OperatorEnum.eq](
          ScanDataEnum.model_name,
          servicesAndMeasuresWithFeatureFilter.currService[
            CubeDimensionEnum.modelName
          ]
        ),
        operatorToExpressionMap[OperatorEnum.eq](
          ScanDataEnum.model_version,
          servicesAndMeasuresWithFeatureFilter.currService[
            CubeDimensionEnum.modelVersion
          ]
        )
      );
    }

    let where = '';
    if (whereStatements.length) {
      where = 'WHERE \n\t';
      where += whereStatements.join('\n\tAND ');
    }

    if (filtersSQL?.length) {
      where += `${where.length ? '\n\tAND' : 'WHERE'} (\n`;
      where += filtersSQL.join('\n\tAND ');
      where += '\n\t)\n';
    }

    let groupBy = '';
    if (group && group.length) {
      groupBy = 'GROUP BY ';
      groupBy += group.join(',\n\t');
    }

    let havingExpr = '';
    if (having) {
      havingExpr = 'HAVING\n\t';
      havingExpr += having
        .map(({ col, value, op }) =>
          whereStatements.push(operatorToExpressionMap[op](col, value))
        )
        .join('\n\tAND ');
    }

    let orderBy = '';
    if (order) {
      orderBy = `ORDER BY \n\t`;
      orderBy += order.map(([col, dir]) => `${col} ${dir}`).join(',\n\t');
    }

    let limitExpr = '';
    if (limit) {
      limitExpr = `LIMIT ${limit}`;
    }

    let offsetExpr = '';
    if (offset) {
      offsetExpr = `OFFSET ${offset}`;
    }

    return `
${select}
FROM ${from || SCAN_DATA_TABLE_NAME}
${where}
${groupBy}
${havingExpr}
${orderBy}
${limitExpr}
${offsetExpr}
    `;
  }, [
    from,
    cols,
    filters,
    group,
    having,
    order,
    limit,
    offset,
    disableClassFilter,
    disableUnitsTransform,
    servicesAndMeasuresWithFeatureFilter.currService,
    units,
    columnsExistInDbData,
  ]);

  const skipCondition =
    skip ||
    (!disableClassFilter &&
      !servicesAndMeasuresWithFeatureFilter.currService) ||
    !scanDataTableExists ||
    !columnsExistInDbData ||
    columnsExistInDbData.isLoading ||
    !!columnsExistInDbData.error;

  // Get the scan data from duckDB filtered by the current feature
  const scanDistributionFilteredByFeature = useDuckQueryScanData<T>(
    query,
    skipCondition
  );

  return scanDistributionFilteredByFeature;
}
